import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import orderStore from "../../stores/order";
import defaultImg from "../../assets/images/default.webp";
import Counter from "../Counter";
import "./style.scss";
import {observer} from "mobx-react";
import {
    CombinationOrderLine,
    CombinationOrderLine_Step,
    HalfAndHalfOrderLine,
    OrderLine
} from "../../@types/stores/order";
import {
    addToLog,
    getCurrency,
    getHost,
    getMaxQuantityInOrderLine,
    isCombinationOrderLine, isHalfAndHalfOrderLine,
    isOrderLine
} from "../../helpers/ApiHelper";
import {hideLoader, showLoader} from "../../helpers/ComponentHelper";
import {
    getAdditionalSellUnitForOrderLine,
    getAddonsFromOrderLine,
    getIngredientsFromOrderLine,
    getModifiersFromOrderLine
} from "../../helpers/orderHelper";
import {ReactComponent as IconPerson} from "../../assets/icons/person.svg";
import {ReactComponent as IconExpand} from "../../assets/icons/expand.svg";
import {ReactComponent as IconEdit} from "../../assets/icons/edit.svg";
import lodash from "lodash";
import {useNavigate} from "react-router";
import { v4 as guid } from 'uuid';
import {ReactComponent as IconWarning} from "../../assets/icons/warning.svg";
import useLayoutStyles from "../../hooks/styles";
import ImgHalfNHalf from "../../assets/images/halfNhalf.jpg";
import menuStore from "../../stores/menu";


const Order = () => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const styles = useLayoutStyles();

    const [openedCombinationIds, setOpenedCombinationIds] = useState<string[]>([]);

    useEffect(() => {
        orderStore.paymentId = "";
        orderStore.changeInternalCard("");

        orderStore.setDefaultPaymentMethodId();
    }, []);

    const changeCount = async (orderLine: OrderLine | CombinationOrderLine | HalfAndHalfOrderLine, value: number) => {
        if (isHalfAndHalfOrderLine(orderLine) && value > 1) {
            return;
        }

        showLoader();

        const maxQuantity = getMaxQuantityInOrderLine();

        value = value > maxQuantity ? maxQuantity : value;

        addToLog(`Change quantity to ${value} for ${orderLine.name}`);

        if (orderLine.count > value)
            await orderStore.subOrderLineQuantity(orderLine.id);
        else
            await orderStore.addOrderLineQuantity(orderLine.id);

        hideLoader();
    }

    const openCombo = (line: CombinationOrderLine) => {
        let opened = lodash.cloneDeep(openedCombinationIds);
        if (opened.indexOf(line.id) === -1)
            opened.push(line.id);
        else
            opened = opened.filter(x => x !== line.id);
        setOpenedCombinationIds(opened);
    }

    const editStep = async (combo: CombinationOrderLine, step: CombinationOrderLine_Step) => {
        showLoader();
        orderStore.editComboId = combo.id;
        combo = lodash.cloneDeep(combo);
        combo.id = guid();
        combo.total = combo.total / combo.count;
        combo.count = 1;
        for (const step of combo.steps) {
            for (const article of step.articles) {
                article.id = guid();
            }
        }
        orderStore.currentCombinationOrderLine = combo;
        hideLoader();

        navigate(`/menu/combination/${combo.menuId}/${combo.subMenuId}/${combo.combinationId}/step/${step.id}/change`);
    }

    const editHalfAndHalf = async (orderLine: HalfAndHalfOrderLine, step: number) => {
        for (let i = 0; i < orderLine.steps.length; i ++) {
            const half = orderLine.steps[i];
            menuStore.selectedHalfAndHalfItems[i] = {
                id: guid(),
                menuId: menuStore.navigation.category,
                subMenuId: menuStore.navigation.subCategory,
                name: half.articles[0].name,
                number: half.articles[0].number,
                count: 0.5,
                price: half.articles[0].price,
                total: half.articles[0].total,
                img: half.articles[0].img,

                modifiers: half.articles[0].modifiers,
                addons: half.articles[0].addons,
                ingredients: half.articles[0].ingredients
            };
        }

        menuStore.selectedHalfAndHalfItems[step] = null;
        menuStore.selectedHalfAndHalfStep = step;
        menuStore.editHalfAndHalfId = orderLine.id;

        navigate(`/menu/halfAndHalf/${orderLine.menuId}/${orderLine.subMenuId}`);
    }

    const getImg = (line: OrderLine | CombinationOrderLine | HalfAndHalfOrderLine) => {
        if (isHalfAndHalfOrderLine(line))
            return ImgHalfNHalf;

        return (line as OrderLine | CombinationOrderLine).img ? `${getHost()}${(line as OrderLine | CombinationOrderLine).img}` : (styles?.article.defaultListImage ? `${getHost()}${styles.article.defaultListImage}` : defaultImg)
    }

    return <div className="Order">
        <h2>{t("yourOrder")}:</h2>
        {orderStore.customer?.name
            ? <div className="customer">
                <IconPerson/> {orderStore.customer.name}
            </div>
            : null
        }
        <div className="orderLines">
            {orderStore.order.lines.map(x => <div key={`orderline_${x.id}`}>
                <div className="orderLine">
                    <div className="img">
                        {isCombinationOrderLine(x) || isHalfAndHalfOrderLine(x)
                            ? <IconExpand
                                className={`expander ${openedCombinationIds.indexOf(x.id) === -1 ? "" : "open"}`}
                                onClick={() => openCombo(x as CombinationOrderLine)}/>
                            : null
                        }
                        <img alt={x.id} src={getImg(x)}/>
                    </div>
                    <div className="text">
                        <div className="title">{x.count} x {x.name}</div>
                        <div className="details" key={x.id}>
                            {isOrderLine(x)
                                ? <>{[getAdditionalSellUnitForOrderLine(x as OrderLine)?.name || ""].filter(x => !!x).concat(getModifiersFromOrderLine(x as OrderLine).map(m => m.name), getAddonsFromOrderLine(x as OrderLine).map(a => a.name), getIngredientsFromOrderLine(x as OrderLine).map(a => a.name)).join(", ")}</>
                                : null
                            }
                        </div>
                    </div>
                    <div className="priceBlock">
                        <div className="price">
                            {x.discount
                                ? <div className="oldPrice">{x.total.toFixed(2)} {getCurrency()}</div>
                                : null
                            }
                            {(x.total - ((x.discount || 0) * x.count)).toFixed(2)} {getCurrency()}</div>
                        <div className="counter">
                            <Counter value={x.count} onChange={(value: number) => changeCount((x as OrderLine | CombinationOrderLine | HalfAndHalfOrderLine), value)}/>
                        </div>
                    </div>
                </div>
                {(isCombinationOrderLine(x) || isHalfAndHalfOrderLine(x)) && openedCombinationIds.indexOf(x.id) !== -1
                    ? Array(x.count).fill(0).map((_, i) => <div key={`comboOrderline_${x.id}_line_${i}`} className="comboOrderLine">
                        <div className="img">
                            <img alt={x.id} src={getImg(x)}/>
                        </div>
                        <div className="text">
                            <div className="title">1 x {x.name}</div>
                            {(x as CombinationOrderLine).steps.map((step, stepNum) => <div key={`orderLine_${x.id}_line_${i}_step_${step.id}`} className="step">
                                <div className="stepTitle">{step.name}</div>
                                <div className="stepData">
                                    <div className="edit">
                                        {isCombinationOrderLine(x)
                                            ? <div className="button" onClick={() => editStep(x as CombinationOrderLine, step)}><IconEdit /></div>
                                            : <div className="button" onClick={() => editHalfAndHalf(x as HalfAndHalfOrderLine, stepNum)}><IconEdit /></div>
                                        }
                                    </div>
                                    <div>
                                        <div className="title">
                                            {step.articles[0].articleId !== -1
                                                ? <>{step.articles[0].count} x {step.articles[0].name}</>
                                                : <>
                                                    <div className="notSelected">{t("notSelected")}</div>
                                                    <div className="noChoiceMade">
                                                        <IconWarning />
                                                        {t("noChoiceMade")}
                                                    </div>
                                                </>
                                            }
                                        </div>
                                        <div className="details">
                                            {[getAdditionalSellUnitForOrderLine(step.articles[0] as OrderLine)?.name || ""].filter(x => !!x).concat(getModifiersFromOrderLine(step.articles[0] as OrderLine).map(m => m.name), getAddonsFromOrderLine(step.articles[0]).map(a => a.name), getIngredientsFromOrderLine(step.articles[0]).map(a => a.name)).join(", ")}
                                        </div>
                                    </div>
                                </div>
                            </div>)}
                        </div>
                        <div className="priceBlock">
                            <div className="price">
                                {x.discount
                                    ? <div className="oldPrice">{x.price.toFixed(2)} {getCurrency()}</div>
                                    : null
                                }
                                {((x.total - (x.discount || 0)) / x.count).toFixed(2)} {getCurrency()}</div>
                        </div>
                    </div>)
                    : null
                }
            </div>)}
        </div>
    </div>
}

export default observer(Order);
